import { useOidcUser } from '@axa-fr/react-oidc';
import { Button, TextInput, Notification } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useQueryClient } from '@tanstack/react-query';
import {
    toastNotifications,
    variants,
    FileUpload,
    FileUploadFile,
    UploadedFile,
    readableFileSize,
    Modal,
} from '@uag/react-core';
import { clsx } from 'clsx';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AttachmentType, OnboardingRequestDetailModel } from 'api/models';
import { useDeleteAttachmentContent, useUploadAttachmentContentByType } from 'api/queries/attachments/attachments';
import {
    getGetRequestByIdQueryKey,
    getGetRequestDetailQueryKey,
    getGetRequestsQueryKey,
    useSaveRequestDraft,
    useSubmitRequest,
} from 'api/queries/requests/requests';
import { getGetStepsQueryKey } from 'api/queries/steps/steps';
import { CompanyBaseData } from './CompanyBaseData';
import { companyToRequest, getInitialRequestValues } from './companyFormMappings';
import { backofficeRequestSchema, requestSchema, RequestSchemaType } from './companyFormSchema';
import { CountryRule, FieldConfigurations } from './CountryRule';

const frontendValidate = zod4Resolver(requestSchema);
const backofficeValidate = zod4Resolver(backofficeRequestSchema);

type Props = {
    request: OnboardingRequestDetailModel;
    className?: string;
    readOnly?: boolean;
    mode?: 'Frontend' | 'Backoffice' | 'Invitation';
    onClose: () => void;
};

const clearInvisibleFields = (values: RequestSchemaType) => {
    // Clone the values to avoid directly mutating the parameter
    const clonedValues = structuredClone(values);
    const countryRule = CountryRule.FindById(clonedValues.companyData.country);

    if (!countryRule) {
        return clonedValues;
    }

    Object.keys(clonedValues.companyData).forEach((key) => {
        if (countryRule.fieldConfigurations[key as keyof FieldConfigurations]?.visible === false) {
            (clonedValues.companyData as Record<string, unknown>)[key] = undefined;
        }
        if (clonedValues.companyData[key as keyof typeof clonedValues.companyData] === '') {
            (clonedValues.companyData as Record<string, unknown>)[key] = undefined;
        }
    });

    return clonedValues;
};

const acceptFileType = 'application/pdf,image/png,image/jpeg,image/tiff,image/heif,image/heic';
const FILE_UPLOAD_MAX_SIZE_BYTES = 10 * 1024 * 1024;

type UploadableAttachment =
    'BusinessLicense' | 'VATCertificate' | 'RegisteredNationalAddress' | 'CommercialRegistration';
export const CompanyForm = ({ request, className, onClose, readOnly = false, mode = 'Frontend' }: Props) => {
    const requestId = request.id;

    const { t } = useTranslation();
    const { oidcUser } = useOidcUser();
    const [isLoading, setIsLoading] = useState(false);

    const initialValues = getInitialRequestValues(request, oidcUser?.email?.toString().split(',')[0]);

    const form = useForm<RequestSchemaType>({
        mode: 'controlled',
        validate: mode === 'Backoffice' ? backofficeValidate : frontendValidate,
        validateInputOnChange: true,
        initialValues,
    });

    const {
        getInputProps,
        onSubmit: onSubmitForm,
        setValues,
        setFieldValue,
        isDirty,
        isValid,
        resetDirty,
        getValues,
    } = form;

    const queryClient = useQueryClient();
    const [previousRequest, setPreviousRequest] = useState(request);

    const [files, setFiles] = useState<Record<UploadableAttachment, UploadedFile | null>>({
        BusinessLicense: null,
        VATCertificate: null,
        RegisteredNationalAddress: null,
        CommercialRegistration: null,
    });

    const [fileErrors, setFileErrors] = useState<Record<UploadableAttachment, string | undefined>>({
        BusinessLicense: undefined,
        VATCertificate: undefined,
        RegisteredNationalAddress: undefined,
        CommercialRegistration: undefined,
    });

    const { mutateAsync: saveRequestDraft } = useSaveRequestDraft();
    const { mutateAsync: submitRequest } = useSubmitRequest();

    const { mutateAsync: uploadAttachment } = useUploadAttachmentContentByType();
    const { mutateAsync: deleteAttachmentContentMutation } = useDeleteAttachmentContent();

    const countryRule = CountryRule.FindById(form.values.companyData.country);

    if (request !== previousRequest) {
        setValues(getInitialRequestValues(request));
        resetDirty();
        setPreviousRequest(request);
    }

    const handleSubmit = async (values: RequestSchemaType) => {
        if (isLoading) {
            return;
        }

        setIsLoading(true);

        const valuesToSave = clearInvisibleFields(values);
        try {
            await handleDeleteFile(valuesToSave);
            if (Object.values(files).some(Boolean)) {
                await uploadFiles(valuesToSave);
            }
            await submitRequest({
                id: request.id,
                data: companyToRequest(valuesToSave),
            });
            resetDirty();

            await queryClient.invalidateQueries({ queryKey: getGetRequestDetailQueryKey(requestId) });
            await queryClient.invalidateQueries({ queryKey: getGetRequestByIdQueryKey(requestId) });
            await queryClient.invalidateQueries({ queryKey: getGetStepsQueryKey(requestId) });
            await queryClient.invalidateQueries({ queryKey: getGetRequestsQueryKey() });

            toastNotifications.success({ title: t('success'), message: t('dataSaved', { data: t('company') }) });

            onClose();
        } catch (e) {
            toastNotifications.errorDetail(
                {
                    title: t('failed'),
                    message: t('dataNotSaved', { data: t('company') }),
                },
                e,
            );
            setIsLoading(false);
        }
    };

    const handleDiscardChanges = () => {
        setValues(getInitialRequestValues(request));
        resetDirty();
        onClose();
    };

    const handleFileChange = (type: UploadableAttachment) => (file: UploadedFile | null) => {
        if (file && file.file.size > FILE_UPLOAD_MAX_SIZE_BYTES) {
            setFileErrors((prev) => ({
                ...prev,
                [type]: t('attachmentTooLarge', { maxSize: readableFileSize(FILE_UPLOAD_MAX_SIZE_BYTES, 0) }),
            }));
            return;
        }
        setFileErrors((prev) => ({ ...prev, [type]: undefined }));
        setFiles((prev) => ({ ...prev, [type]: file }));
        setFieldValue(type, file ? file.file.name : undefined);
    };

    const handleFileRemove = (type: UploadableAttachment) => () => {
        setFileErrors((prev) => ({ ...prev, [type]: undefined }));
        setFiles((prev) => ({ ...prev, [type]: null }));
        setFieldValue(type, undefined);
    };

    const handleDeleteFile = async (formValues: RequestSchemaType) => {
        const toDelete = getRemovedAttachments(formValues);
        for (const type of toDelete) {
            const attachment = request.attachments?.find((att) => att.attachmentType === AttachmentType[type]);
            if (attachment?.id) {
                await deleteAttachmentContentMutation({
                    id: requestId,
                    attachmentId: attachment.id,
                });
            }
        }
    };

    const handleSaveDraft = async (values: RequestSchemaType) => {
        if (isLoading) {
            return;
        }

        setIsLoading(true);

        clearInvisibleFields(values);
        try {
            await handleDeleteFile(values);
            if (Object.values(files).some(Boolean)) {
                await uploadFiles(values);
            }
            await saveRequestDraft({
                id: requestId,
                data: companyToRequest(values),
            });
            resetDirty();
            await queryClient.invalidateQueries({ queryKey: ['get-attachments', requestId] });
            await queryClient.invalidateQueries({ queryKey: getGetRequestDetailQueryKey(requestId) });
            await queryClient.invalidateQueries({ queryKey: getGetStepsQueryKey(requestId) });
            toastNotifications.success({ title: t('success'), message: t('dataSaved', { data: t('company') }) });
            onClose();
        } catch (e) {
            toastNotifications.errorDetail(
                {
                    title: t('failed'),
                    message: t('dataNotSaved', { data: t('company') }),
                },
                e,
            );
            setIsLoading(false);
        }
    };

    const uploadFiles = async (values: RequestSchemaType) => {
        const uploadableTypes: UploadableAttachment[] = [
            'BusinessLicense',
            'VATCertificate',
            'RegisteredNationalAddress',
            'CommercialRegistration',
        ];

        for (const type of uploadableTypes) {
            const file = files[type];
            const valueInForm = values[type];

            if (!file || !valueInForm) {
                continue;
            }

            await uploadAttachment({
                id: requestId,
                attachmentType: type,
                data: { File: file.file },
            });
        }
    };

    const getRemovedAttachments = (formValues: RequestSchemaType): UploadableAttachment[] => {
        return (
            [
                'BusinessLicense',
                'VATCertificate',
                'RegisteredNationalAddress',
                'CommercialRegistration',
            ] as UploadableAttachment[]
        ).filter((type) => {
            const attachmentExistsInRequest = request.attachments?.some(
                (att) => att.attachmentType === AttachmentType[type] && att.exists,
            );

            const isNowMissingInForm = !formValues[type];

            return attachmentExistsInRequest && isNowMissingInForm;
        });
    };

    const getDisplayedFile = (type: UploadableAttachment): FileUploadFile[] => {
        const file = files[type];
        if (file) {
            return [{ name: file.file.name, size: file.file.size }];
        }

        const valueInForm = form.values[type];
        if (!valueInForm) {
            return [];
        }

        const attachment = request.attachments?.find(
            (att) => att.attachmentType === AttachmentType[type] && att.exists,
        );

        if (attachment) {
            return [{ name: attachment.fileName ?? '', size: attachment.fileSizeBytes ?? undefined }];
        }

        return [];
    };

    return (
        <form className={clsx(className, 'flex w-[646px] flex-col gap-4')} onSubmit={onSubmitForm(handleSubmit)}>
            <CompanyBaseData form={form} readOnly={readOnly} />
            {form.values.companyData.country && (
                <>
                    <h4 className="text-base-bold mt-6 -mb-2">{t('keyUser')}</h4>
                    <TextInput
                        description={t('technicalAdministratorDescription')}
                        label={t('technicalAdministrator')}
                        placeholder={t('enterPlaceholder', { label: t('technicalAdministrator') })}
                        readOnly={readOnly}
                        required
                        {...form.getInputProps('technicalAdministrator')}
                    />
                </>
            )}
            {mode === 'Frontend'
                && !request.existingCustomer
                && countryRule?.fieldConfigurations.businessLicenseUpload.visible && (
                    <>
                        <h4 className="text-base-bold mt-6 -mb-2">{t('documents')}</h4>
                        <FileUpload
                            accept={acceptFileType}
                            description={t('documentUploadDescription')}
                            files={getDisplayedFile('BusinessLicense')}
                            label={t('businessLicence')}
                            required={
                                countryRule?.fieldConfigurations.businessLicenseUpload.mandatory
                                && !request.existingCustomer
                            }
                            {...getInputProps('BusinessLicence')}
                            error={fileErrors.BusinessLicense}
                            mode="single"
                            onFileSelected={handleFileChange('BusinessLicense')}
                            onRemoveFile={handleFileRemove('BusinessLicense')}
                        />
                    </>
                )}
            {mode === 'Frontend'
                && !request.existingCustomer
                && countryRule?.fieldConfigurations.commercialRegistrationUpload.visible && (
                    <>
                        <h4 className="text-base-bold mt-6 -mb-2">{t('documents')}</h4>
                        <FileUpload
                            accept={acceptFileType}
                            description={t('documentUploadDescription')}
                            files={getDisplayedFile('CommercialRegistration')}
                            label={t('CommercialRegistration')}
                            required={
                                countryRule?.fieldConfigurations.commercialRegistrationUpload.mandatory
                                && !request.existingCustomer
                            }
                            {...getInputProps('CommercialRegistration')}
                            error={fileErrors.CommercialRegistration}
                            mode="single"
                            onFileSelected={handleFileChange('CommercialRegistration')}
                            onRemoveFile={handleFileRemove('CommercialRegistration')}
                        />
                    </>
                )}

            {mode === 'Frontend'
                && !request.existingCustomer
                && countryRule?.fieldConfigurations.VATCertificateUpload.visible && (
                    <FileUpload
                        accept={acceptFileType}
                        description={t('documentUploadDescription')}
                        files={getDisplayedFile('VATCertificate')}
                        label={t('vatCertificate')}
                        required={countryRule?.fieldConfigurations.VATCertificateUpload.mandatory}
                        {...getInputProps('VATCertificate')}
                        error={fileErrors.VATCertificate}
                        mode="single"
                        onFileSelected={handleFileChange('VATCertificate')}
                        onRemoveFile={handleFileRemove('VATCertificate')}
                    />
                )}
            {mode === 'Frontend'
                && !request.existingCustomer
                && countryRule?.fieldConfigurations.RegisteredNationalAddressUpload.visible && (
                    <FileUpload
                        accept={acceptFileType}
                        description={t('documentUploadDescription')}
                        files={getDisplayedFile('RegisteredNationalAddress')}
                        label={t('RegisteredNationalAddress')}
                        required={
                            countryRule?.fieldConfigurations.RegisteredNationalAddressUpload.mandatory
                            && !request.existingCustomer
                        }
                        {...getInputProps('RegisteredNationalAddress')}
                        error={fileErrors.RegisteredNationalAddress}
                        mode="single"
                        onFileSelected={handleFileChange('RegisteredNationalAddress')}
                        onRemoveFile={handleFileRemove('RegisteredNationalAddress')}
                    />
                )}

            {!isValid() && !isLoading && form.values.companyData.country && (
                <Notification
                    classNames={{ body: 'flex-wrap' }}
                    title={t('completeAllData')}
                    variant={variants.notification.inlineNotificationAlert}
                    withCloseButton={false}
                >
                    {t('completeAllDataMessage')}
                </Notification>
            )}

            <Modal.Footer>
                {mode === 'Frontend' && (
                    <>
                        <Button
                            disabled={!isDirty() || isLoading}
                            loading={isLoading}
                            variant={variants.button.secondary}
                            onClick={() => handleSaveDraft(getValues())}
                        >
                            {t('saveDraft')}
                        </Button>
                        <Button disabled={!isValid() || isLoading} loading={isLoading} type="submit">
                            {t('submit')}
                        </Button>
                    </>
                )}
                {mode === 'Backoffice' && !readOnly && (
                    <>
                        <Button
                            disabled={!isDirty() || isLoading}
                            loading={isLoading}
                            variant={variants.button.secondary}
                            onClick={handleDiscardChanges}
                        >
                            {t('discardChanges')}
                        </Button>
                        <Button disabled={!isValid() || !isDirty() || isLoading} loading={isLoading} type="submit">
                            {t('saveChanges')}
                        </Button>
                    </>
                )}
            </Modal.Footer>
        </form>
    );
};
